import React from "react";
import { productSearchStore as store } from "../ProductSearchStore";
import ProductRow from "./ProductRow";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import LoadingIcon from "components/LoadingIcon";
import RowHoverActions from "../components/RowHoverActions";
import { MEStore } from "features/MappingEditor/MappingEditorStore";
import { ProductSearchProps } from "..";
import { SingleItem } from "api-client";
import LoadMoreIndicator from "../components/LoadMoreIndicator";
import TableFooter from "../components/TableFooter";
import IconAI from "assets/images/icons/concular-ai.svg";
import clsx from "clsx";
import EmptyState from "../components/EmptyState";

interface SearchResultsProps extends ProductSearchProps {
  closeModal?: () => void;
}

const SearchResults = ({ ...props }: SearchResultsProps) => {
  const { t } = useTranslation();
  const loadedRowsCount = store.searchResults.count + store.usedProducts.count;

  const onMouseScroll = async (e: React.UIEvent<HTMLDivElement, UIEvent>) => {
    e.stopPropagation();
    const { scrollHeight, scrollTop, clientHeight } =
      e.target as HTMLDivElement;

    if (scrollTop + clientHeight >= scrollHeight - 5) {
      await store.fetchNextPage();
    }
  };

  function loadingView() {
    return (
      <div className="absolute top-0 h-full left-0 flex w-full items-center justify-center bg-white z-3">
        <LoadingIcon type="secondary" className="!w-20 !h-20" />
      </div>
    );
  }

  const matchProduct = async (product_id: string) => {
    props.isDetailVeiw
      ? await MEStore.detailViewMapping(product_id)
      : await props.matchProductAndUpdate?.(
          product_id,
          props.currentRow as SingleItem
        );
    await props.refreshAfterMatching?.();
    props.closeModal?.();
  };

  function suggestedRows() {
    if (store.suggestedProducts.count === 0) return;

    return (
      <>
        {store.suggestedProducts.count > 0 && (
          <div
            className={clsx(
              "bg-green-50 border-b border-gray-200 text-gray-500 px-4 py-1 text-xs font-medium",
              "flex items-center gap-2"
            )}
          >
            <div>{<img src={IconAI} alt="concular-ai" />}</div>
            {t("productSearch.suggestedProducts")}
          </div>
        )}

        {store.suggestedProducts.items.map((item) => (
          <ProductRow
            key={item.id}
            product={item}
            settings={<RowHoverActions type="suggested" product={item} />}
            match={matchProduct}
          />
        ))}
      </>
    );
  }

  function usedProductsRows() {
    return (
      <>
        {store.usedProducts.count > 0 && (
          <div className="bg-gray-50 border-b border-gray-200 text-gray-500 px-4 py-2 text-xs font-medium">
            {t("productSearch.lastUsed")}
          </div>
        )}

        {store.usedProducts.items.map((item) => (
          <ProductRow
            key={item.id}
            product={item}
            settings={<RowHoverActions type="usedProducts" product={item} />}
            match={matchProduct}
          />
        ))}
      </>
    );
  }

  function productRows() {
    return (
      <>
        {store.searchResults.count > 0 && !store.searchInput.product_source && (
          <div className="bg-gray-50 border-b border-gray-200 text-gray-500 px-4 py-2 text-xs font-medium">
            {t("productSearch.all")}
          </div>
        )}

        {store.searchResults.items.map((item) => (
          <ProductRow
            key={item.id}
            product={item}
            settings={<RowHoverActions product={item} />}
            match={matchProduct}
          />
        ))}
      </>
    );
  }

  const showPageLoading =
    store.productSearchLoading && store.currentSearchPage === 1;

  if (!store.productSearchLoading && loadedRowsCount === 0) {
    return <EmptyState />;
  }

  return (
    <div className="relative border border-gray-300 rounded-md overflow-hidden">
      {showPageLoading && loadingView()}

      <div
        className="flex-grow overflow-y-auto min-h-[calc(100vh-290px)] max-h-[calc(100vh-290px)] mb-8 relative"
        onScroll={onMouseScroll}
      >
        {suggestedRows()}

        {usedProductsRows()}

        {productRows()}

        <LoadMoreIndicator
          open={store.productSearchLoading && loadedRowsCount > 0}
        />
      </div>

      {!showPageLoading && (
        <TableFooter
          count={loadedRowsCount}
          total={store.searchResults.total + store.usedProducts.total}
        />
      )}
    </div>
  );
};

export default observer(SearchResults);
